import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import { getAdSessionId, recordImpression } from '@/lib/adSession';

interface Campaign {
  id: string;
  image_url: string;
  target_url: string | null;
  advertiser_name: string | null;
  title: string | null;
}

interface Props {
  /** Listing page slug — used to match banners targeted to this page. */
  page?: 'buy' | 'rent' | 'commercial' | 'plots' | 'projects' | string;
  /** Max number of stacked banners to show. */
  limit?: number;
}

const trackedImpressions = new Set<string>();
const trackedClicks = new Set<string>();

/**
 * Simple "image + link" sidebar ad stack for search-result pages.
 * Ad Operations Manager only provides the creative image and the click-through URL —
 * no titles, prices, CTAs, or extra chrome are rendered.
 *
 * Pulls active banners from `ad_campaigns` using placement code `n`
 * (the right-sidebar placement used across listing pages).
 */
export function ProjectAdsSidebar({ page = 'buy', limit = 15 }: Props) {
  const sessionId = useMemo(() => getAdSessionId(), []);
  const today = new Date().toISOString().slice(0, 10);
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await (supabase as any)
        .from('ad_campaigns')
        .select('id,title,image_url,target_url,advertiser_name,pages,placement,status,is_active,start_date,end_date')
        .eq('placement', 'right_sidebar')
        .eq('is_active', true)
        .lte('start_date', today)
        .gte('end_date', today)
        .order('created_at', { ascending: false })
        .limit(20);
      if (cancelled) return;
      const filtered = (data || []).filter((c: any) => {
        if (c.status && !['active', 'approved', 'live'].includes(c.status)) return false;
        const pages = Array.isArray(c.pages) ? c.pages : [];
        if (pages.length && !pages.includes(page) && !pages.includes('all')) return false;
        return !!c.image_url;
      }).slice(0, limit);
      setCampaigns(filtered);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [page, today, limit]);

  // Log impressions once per session per ad
  useEffect(() => {
    if (!campaigns) return;
    campaigns.forEach((c) => {
      const k = c.id + '|right_sidebar';
      if (trackedImpressions.has(k)) return;
      trackedImpressions.add(k);
      recordImpression(c.id, 'right_sidebar');
      (supabase as any).from('ad_events').insert({
        campaign_id: c.id, event_type: 'impression', placement: 'right_sidebar',
        page, session_id: sessionId,
      }).then?.(() => {}, () => {});
    });
  }, [campaigns, page, sessionId]);

  const handleClick = (c: Campaign) => {
    const k = c.id + '|right_sidebar';
    if (trackedClicks.has(k)) return;
    trackedClicks.add(k);
    (supabase as any).from('ad_events').insert({
      campaign_id: c.id, event_type: 'click', placement: 'right_sidebar',
      page, session_id: sessionId,
    }).then?.(() => {}, () => {});
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <Skeleton key={i} className="aspect-[4/3] w-full rounded-lg" />)}
      </div>
    );
  }

  if (!campaigns?.length) return null;

  return (
    <div className="space-y-4">
      {campaigns.map((c) => {
        const img = (
          <img
            src={c.image_url}
            alt={c.title || c.advertiser_name || 'Advertisement'}
            loading="lazy"
            className="w-full h-auto rounded-lg border bg-card object-cover hover:opacity-95 transition-opacity"
          />
        );
        return c.target_url ? (
          <a
            key={c.id}
            href={c.target_url}
            target="_blank"
            rel="noopener sponsored"
            onClick={() => handleClick(c)}
            className="block"
            aria-label={c.title || c.advertiser_name || 'Sponsored'}
          >
            {img}
          </a>
        ) : (
          <div key={c.id}>{img}</div>
        );
      })}
      <p className="text-[10px] text-muted-foreground/70 text-center uppercase tracking-wider">Advertisement</p>
    </div>
  );
}
